/**
 * Overview tab (§4.4.1).
 *
 * Stat grid (status / uptime / PID / RAM / last error / message counters)
 * fed by the polling `useAgentStatus` hook. Danger zone at the bottom
 * houses the Delete agent flow.
 */
import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { AlertTriangle, Trash2 } from 'lucide-react';
import type { AgentConfig } from '@/api/agents';
import { useAgentStatus } from '@/hooks/useAgentStatus';
import { useDeleteAgent } from '@/hooks/useAgents';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { LoadingSkeleton } from '@/components/LoadingSkeleton';
import { StatusBadge } from '@/components/StatusBadge';
import { DangerZone } from '@/components/DangerZone';
import { ConfirmDialog } from '@/components/ConfirmDialog';
import { StepUpDialog } from '@/components/StepUpDialog';
import { formatBytes, formatDuration, formatRelative } from '@/lib/format';
import { ApiError } from '@/api/client';

interface OverviewProps {
  agent: AgentConfig;
}

export default function Overview({ agent }: OverviewProps) {
  const status = useAgentStatus(agent.name);
  const del = useDeleteAgent();
  const navigate = useNavigate();

  const [confirmOpen, setConfirmOpen] = useState(false);
  const [stepUpOpen, setStepUpOpen] = useState(false);
  const [deleteError, setDeleteError] = useState<string | null>(null);

  function handleConfirmedDelete() {
    setStepUpOpen(true);
  }

  async function handleStepUp(token: string) {
    try {
      await del.mutateAsync({ name: agent.name, stepupToken: token });
      setConfirmOpen(false);
      setDeleteError(null);
      navigate('/agents');
    } catch (err) {
      setDeleteError(err instanceof ApiError ? err.message : 'Failed to delete agent');
    }
  }

  const s = status.data;
  const loading = status.isLoading;

  return (
    <div className="space-y-6">
      <section
        aria-label="Agent stats"
        className="grid grid-cols-2 gap-4 md:grid-cols-3 xl:grid-cols-4"
      >
        <StatCard
          label="Status"
          body={
            loading ? (
              <LoadingSkeleton className="h-5 w-24" />
            ) : s ? (
              <StatusBadge status={s.status} />
            ) : (
              <span className="text-muted-foreground">—</span>
            )
          }
        />
        <StatCard
          label="Uptime"
          body={
            loading ? (
              <LoadingSkeleton className="h-5 w-20" />
            ) : (
              <span className="text-lg font-semibold">
                {formatDuration(s?.uptime_seconds ?? null)}
              </span>
            )
          }
        />
        <StatCard
          label="Main PID"
          body={
            loading ? (
              <LoadingSkeleton className="h-5 w-16" />
            ) : (
              <span className="font-mono text-lg">{s?.pid ?? '—'}</span>
            )
          }
        />
        <StatCard
          label="Memory"
          body={
            loading ? (
              <LoadingSkeleton className="h-5 w-20" />
            ) : (
              <span className="text-lg font-semibold">
                {formatBytes(s?.memory_bytes ?? null)}
              </span>
            )
          }
        />
        <StatCard
          label="Messages today"
          body={
            loading ? (
              <LoadingSkeleton className="h-5 w-12" />
            ) : (
              <span className="text-lg font-semibold">{s?.messages_today ?? 0}</span>
            )
          }
        />
        <StatCard
          label="Messages this week"
          body={
            loading ? (
              <LoadingSkeleton className="h-5 w-12" />
            ) : (
              <span className="text-lg font-semibold">{s?.messages_week ?? 0}</span>
            )
          }
        />
        <StatCard
          label="Messages total"
          body={
            loading ? (
              <LoadingSkeleton className="h-5 w-12" />
            ) : (
              <span className="text-lg font-semibold">{s?.messages_total ?? 0}</span>
            )
          }
        />
        <StatCard
          label="Last message"
          body={
            loading ? (
              <LoadingSkeleton className="h-5 w-20" />
            ) : (
              <span className="text-sm">
                {s?.last_message_at ? formatRelative(s.last_message_at) : 'Never'}
              </span>
            )
          }
        />
      </section>

      {s?.last_error && (
        <Card className="border-destructive/40">
          <CardHeader className="flex-row items-center gap-2 space-y-0 pb-2">
            <AlertTriangle className="h-4 w-4 text-destructive" />
            <CardTitle className="text-sm">Last error</CardTitle>
            {s.last_error_at && (
              <span className="ml-auto text-xs text-muted-foreground">
                {formatRelative(s.last_error_at)}
              </span>
            )}
          </CardHeader>
          <CardContent>
            <pre className="whitespace-pre-wrap break-words font-mono text-xs text-destructive">
              {s.last_error}
            </pre>
          </CardContent>
        </Card>
      )}

      {s?.session_file && (
        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-sm">Session file</CardTitle>
          </CardHeader>
          <CardContent className="space-y-1 font-mono text-xs">
            <div>{s.session_file}</div>
            <div className="text-muted-foreground">
              {formatBytes(s.session_file_bytes ?? 0)}
            </div>
          </CardContent>
        </Card>
      )}

      <DangerZone>
        <div className="flex items-center justify-between gap-4">
          <div>
            <h4 className="font-semibold">Delete this agent</h4>
            <p className="text-sm text-muted-foreground">
              This will stop the service, remove the unit file, and delete its
              config entry. Memory files are retained on disk.
            </p>
          </div>
          <Button
            variant="destructive"
            onClick={() => setConfirmOpen(true)}
          >
            <Trash2 className="h-4 w-4" />
            Delete…
          </Button>
        </div>
        {deleteError && (
          <p className="text-sm text-destructive" role="alert">
            {deleteError}
          </p>
        )}
      </DangerZone>

      <ConfirmDialog
        open={confirmOpen}
        onOpenChange={setConfirmOpen}
        title="Delete agent"
        description={`This will permanently delete ${agent.name}. This action cannot be undone.`}
        confirmText={agent.name}
        confirmLabel="Delete"
        variant="destructive"
        onConfirm={handleConfirmedDelete}
      />
      <StepUpDialog
        open={stepUpOpen}
        onOpenChange={setStepUpOpen}
        onSuccess={handleStepUp}
      />
    </div>
  );
}

function StatCard({ label, body }: { label: string; body: React.ReactNode }) {
  return (
    <Card>
      <CardHeader className="pb-2">
        <CardTitle className="text-xs font-medium uppercase tracking-wide text-muted-foreground">
          {label}
        </CardTitle>
      </CardHeader>
      <CardContent>{body}</CardContent>
    </Card>
  );
}
